import { DateTime } from 'luxon'

/**
 * The three shapes a date projection reduces a timestamp to. Each is uniform — every value covers the
 * same span — which is what lets an axis built from them be filled in and evenly spaced. A raw
 * timestamp grouping has no granularity, so nothing here applies to it.
 */
export type DateGranularity = 'year' | 'quarter' | 'month'

const PATTERNS: Record<DateGranularity, RegExp> = {
  year: /^\d{4}$/,
  quarter: /^\d{4}-Q[1-4]$/,
  month: /^\d{4}-(0[1-9]|1[0-2])$/
}

/** Shared with the projections, so a padded interval is named exactly as a populated one is. */
export const granularityLabel = (value: string, granularity: DateGranularity): string => {
  switch (granularity) {
    case 'year':
      return value
    case 'quarter':
      return `Q${value.slice(6)} ${value.slice(0, 4)}`
    case 'month': {
      const parsed = DateTime.fromFormat(value, 'yyyy-MM')
      return parsed.isValid ? parsed.toFormat('MMM yyyy') : value
    }
  }
}

/** The granularity every value shares, or null when they disagree or are not projected dates. */
export const detectGranularity = (values: string[]): DateGranularity | null => {
  if (values.length === 0) return null
  const found = (Object.keys(PATTERNS) as DateGranularity[]).find((g) => values.every((v) => PATTERNS[g].test(v)))
  return found ?? null
}

const toDate = (value: string, granularity: DateGranularity): DateTime =>
  granularity === 'quarter'
    ? DateTime.fromObject({ year: Number(value.slice(0, 4)), month: (Number(value.slice(6)) - 1) * 3 + 1 })
    : DateTime.fromFormat(value, granularity === 'year' ? 'yyyy' : 'yyyy-MM')

const toValue = (date: DateTime, granularity: DateGranularity): string => {
  switch (granularity) {
    case 'year':
      return date.toFormat('yyyy')
    case 'quarter':
      return `${date.toFormat('yyyy')}-Q${date.quarter}`
    case 'month':
      return date.toFormat('yyyy-MM')
  }
}

const STEP: Record<DateGranularity, Parameters<DateTime['plus']>[0]> = {
  year: { years: 1 },
  quarter: { quarters: 1 },
  month: { months: 1 }
}

/**
 * Every value from the first to the last, including those nothing was measured in — so a chart axis
 * spans the whole period at an even step instead of closing the gaps where no sample happened to fall.
 *
 * Capped, because a projection can be left on `Month` over a decade of orders; past the cap the
 * original values are returned rather than several hundred mostly-empty intervals.
 */
export const MAX_FILLED_INTERVALS = 240

export const fillGranularity = (values: string[], granularity: DateGranularity): string[] => {
  const dates = values.map((v) => toDate(v, granularity)).filter((d) => d.isValid)
  if (dates.length === 0) return values

  /* reduce rather than DateTime.min/max, whose return widens to undefined for a possibly-empty list */
  const first = dates.reduce((a, b) => (b < a ? b : a))
  const last = dates.reduce((a, b) => (b > a ? b : a))
  const filled: string[] = []

  for (let at = first; at <= last; at = at.plus(STEP[granularity])) {
    filled.push(toValue(at, granularity))
    if (filled.length > MAX_FILLED_INTERVALS) return values
  }

  return filled
}
